import { useCallback, useEffect, useState } from 'react';
import type { PeopleReply, PersonAction, PersonView } from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { api } from '../lib/api';

// who the player knows and who is here, refreshed on demand; every action is sent to the server, and the
// person in the reply replaces what the screen showed
// inside a venue the list is looked at more often, so people walking in show up soon
export function usePeople(enabled: boolean, placeId: string | null, onChanged?: () => void, inVenue = false) {
  const [reply, setReply] = useState<PeopleReply | null>(null);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!enabled) return setReply(null);
    setFailure(null);
    try {
      setReply(await api.people.get());
    } catch (error) {
      setFailure(describeError(error));
    }
  }, [enabled]);
  useEffect(() => {
    void load();
  }, [load, placeId]);
  // who is here changes as people come and go: look again every minute (every 20 seconds in a venue)
  useEffect(() => {
    if (!enabled) return;
    if (inVenue) void load();
    const timer = setInterval(() => void load(), inVenue ? 20_000 : 60_000);
    return () => clearInterval(timer);
  }, [enabled, load, inVenue]);
  const act = useCallback(
    async (lifeId: string, body: PersonAction) => {
      setBusy(true);
      setFailure(null);
      try {
        const next = await api.people.act(
          lifeId,
          body,
          body.action === 'social' ? `social-${crypto.randomUUID()}` : undefined,
        );
        const replace = (list: PersonView[]) => list.map((person) => (person.lifeId === lifeId ? next.person : person));
        setReply((current) =>
          current
            ? {
                ...current,
                people: current.people.some((person) => person.lifeId === lifeId)
                  ? replace(current.people)
                  : [...current.people, next.person],
                here: next.person.blocked
                  ? current.here.filter((person) => person.lifeId !== lifeId)
                  : replace(current.here),
              }
            : current,
        );
        setMessage(next.message);
        onChanged?.();
        return true;
      } catch (error) {
        setFailure(describeError(error));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [onChanged],
  );
  return { reply, failure, busy, message, clearMessage: () => setMessage(null), act, reload: load };
}
